import { Suspense, forwardRef, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FormikProps } from "formik"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { CreateProduct, CreateUpdateProductFormValues } from "../productsTypes"
import { toProductFormValues } from "../productFormValues"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { GetProductDetailsResponse, updateProduct, getProductDetails, MerchantProductsQuery } from "../productsApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import {
    MERCHANT_ADMIN_PRODUCTS_LIST_ROUTE,
    MERCHANT_ADMIN_ROUTE,
    getProductsListRoute,
} from "../../../../../constants/routes"
import { CreateUpdateProductForm } from "../components/CreateUpdateProductForm"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardLoadingButton } from "../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { DateTimeFactory, useDateTime } from "../../../../localization/useDateTime"

const selectProduct = (
    data: GetProductDetailsResponse,
    dateTimeFactory: DateTimeFactory,
): CreateUpdateProductFormValues | null => (data.product ? toProductFormValues(data.product, dateTimeFactory) : null)

type ProductProps = Readonly<{
    onLoadingChange: (loading: boolean) => void
}>

const Product = forwardRef<FormikProps<CreateUpdateProductFormValues>, ProductProps>(({ onLoadingChange }, ref) => {
    const { productId } = useParams()
    const navigate = useNavigate()
    const dateTimeFactory = useDateTime()

    const productResult = useApiQuery(getProductDetails, {
        queryName: MerchantProductsQuery.PRODUCT_DETAILS,
        dependencies: {
            productId: Number(productId) ?? 0,
        },
        enabled: Boolean(productId),
        select: (data) => selectProduct(data, dateTimeFactory),
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateProduct } = useApiMutation(updateProduct, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(MerchantProductsQuery.PRODUCT_DETAILS, { productId: params.productId }),
            })
            navigate(getProductsListRoute(params.product.priceLists?.[0]?.id))
        },
    })

    const handleSubmit = async (data: CreateProduct) => {
        if (!productId) return
        onLoadingChange(true)
        try {
            await callUpdateProduct({ product: data, productId: Number(productId) })
        } finally {
            onLoadingChange(false)
        }
    }

    return (
        <>
            <CreateUpdateProductForm ref={ref} initialValues={productResult.data} onSubmit={handleSubmit} />
        </>
    )
})

export function EditProductPage() {
    const productFormRef = useRef<FormikProps<CreateUpdateProductFormValues>>(null)
    const [isSaving, setIsSaving] = useState(false)

    const handleSaveClick = () => {
        if (productFormRef.current) {
            productFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="product-edit-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_PRODUCTS}
                    title="Edit product"
                    rightElement={
                        <LocalizedStrict id="product-edit-submit-button" attrs={{ loadingLabel: true }}>
                            <StandardLoadingButton
                                loading={isSaving}
                                loadingLabel="Saving..."
                                type="submit"
                                onClick={handleSaveClick}
                            >
                                Save
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PRODUCTS_LIST_ROUTE}>Products</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-update">
                            <Breadcrumb>Edit product</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Product ref={productFormRef} onLoadingChange={setIsSaving} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
